import { useState, useEffect, useLayoutEffect, useRef } from "react";
import crudApi from "@serie3/common/api/crudApi";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import { isOverlayActive } from "@serie3/mymediaplanner/Modules/Social/Domain/overlayRenderer";
import OverlayView from "../Overlay/OverlayView";

/**
 * Preview of one attached file.
 *
 * Images with an active title are drawn through OverlayView — the same renderer
 * the editor and the publish-time screenshot use, so the preview matches what
 * goes out. The overlay lives on the post/image connection, so the stored file is
 * untouched and the preview is always composed live.
 */
const PicturePreview = ({
  file,
  binaryEndpoint = DocumentsEndpoints.show,
  post = null,
}) => {
  const [picture, setPicture] = useState(null);
  const [video, setVideo] = useState(null);
  const [application, setApplication] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const containerRef = useRef(null);

  const title = post?.title;
  const showOverlay = isOverlayActive(file?.overlay, title);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const getImage = async () => {
      const response = await crudApi.getBinary(binaryEndpoint + file.document.id);
      if (cancelled || response.status !== 200) return;

      const contentType = response.headers["content-type"];
      objectUrl = window.URL.createObjectURL(response.data);

      if (contentType.startsWith("image/")) {
        // The natural size sets the stage's aspect ratio.
        const probe = new Image();
        probe.onload = () => {
          if (cancelled) return;
          setNaturalSize({ width: probe.naturalWidth, height: probe.naturalHeight });
          setPicture(objectUrl);
        };
        probe.onerror = () => {
          if (!cancelled) setPicture(objectUrl);
        };
        probe.src = objectUrl;
      } else if (contentType.startsWith("video/")) {
        setVideo(objectUrl);
      } else if (contentType.startsWith("application/")) {
        setApplication(objectUrl);
      } else {
        console.error("Unsupported media type:", contentType);
      }
    };

    if (file.document && file.document.id) {
      getImage();
    }

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file, binaryEndpoint]);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const measure = () => setBoxWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [picture]);

  const canCompose = showOverlay && naturalSize && boxWidth > 0;

  return (
    <div ref={containerRef}>
      {picture && canCompose && (
        <OverlayView
          overlay={file.overlay}
          defaultCss={file.defaultCss}
          title={title}
          src={picture}
          imageSize={naturalSize}
          displayWidth={boxWidth}
          style={{ borderRadius: "1.6rem" }}
        />
      )}

      {picture && !canCompose && (
        <img
          src={picture}
          alt="media content"
          style={{ borderRadius: "1.6rem", maxWidth: "100%" }}
        />
      )}

      {video && (
        <video controls width="100%">
          <source src={video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {application && (
        <embed src={application} type="application/pdf" width="100%" height="600px" />
      )}
    </div>
  );
};

export default PicturePreview;
